// 添加浏览器兼容性 polyfill（必须在最顶部）
// 导入所有 core-js 提供的 polyfill，包括最新的标准和提案
import 'core-js/actual'

import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import $ from 'jquery'
import './styles.css'
import reportWebVitals from './reportWebVitals.ts'
import Watermark from '@/Watermark.tsx' // 引入水印组件
import { shouldShowWatermark } from './utils/watermark.ts'
import * as Sentry from '@sentry/react'
import { REFRESH_TOKEN_KEY } from './types/info.ts'
import { getUserIdFromRefreshToken } from './utils/jwt.ts'
import { CommonErrorMsg } from './types/error.ts'
import { clearStacks, getReqStack, getResStack } from './utils/http.ts'
// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    ...TanStackQueryProvider.getContext(),
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})
/**
 * 创建Sentry的beforeSend处理器
 * 使用NonNullable排除undefined类型，确保Parameters能正常工作
 */
const prodEnvSentryDns =
  'https://060a44dcba5af9b9fa6c78e2265ae5b5@o4510718433820672.ingest.de.sentry.io/4510718435393616'
const beforeSend = (() => {
  if (
    window.location.hostname.includes('localhost') ||
    window.location.hostname.startsWith('192.168.')
  ) {
    // 使用NonNullable工具类型排除undefined，确保Parameters能正确获取函数参数类型
    return (
      ...args: Parameters<NonNullable<Sentry.BrowserOptions['beforeSend']>>
    ) => {
      const [event, hint] = args
      console.log('sentry error', event, hint)
      return null
    }
  } else {
    return (
      ...args: Parameters<NonNullable<Sentry.BrowserOptions['beforeSend']>>
    ) => {
      const [event, hint] = args
      //添加token区分用户
      const token = localStorage.getItem(REFRESH_TOKEN_KEY)
      if (token) {
        event.user = {
          id: getUserIdFromRefreshToken(token),
        }
      }
      // 添加请求信息
      const [httpsSuccessResStack, httpsErrorResStack] = getResStack()
      const reqStack = getReqStack()
      // 将所有请求和响应信息添加到事件中
      event.extra = {
        axiosSuccessResponses: httpsSuccessResStack.map((res) => ({
          url: res.config.url,
          status: res.status,
          code: res.data.code,
          data: res.data,
          headers: res.headers,
          reqid: res.headers['x-fc-request-id'],
          request: res.config,
          time: new Date().toLocaleString(),
        })),
        apiErrorResponses: httpsSuccessResStack
          .filter((res) => {
            return res.data.code.toString() !== '200'
          })
          .map((res) => ({
            url: res.config.url,
            status: res.status,
            code: res.data.code,
            data: res.data,
            headers: res.headers,
            reqid: res.headers['x-fc-request-id'],
            request: res.config,
            time: new Date().toLocaleString(),
          })),
        axiosErrorResponses: httpsErrorResStack.map((axiosError) => ({
          url: axiosError.request?.url,
          status: axiosError.response?.status,
          data: axiosError.response?.data,
          headers: axiosError.response?.headers,
          reqid: axiosError.response?.headers['x-fc-request-id'],
          request: axiosError.request,
          time: new Date().toLocaleString(),
        })),
        requests: reqStack.map((req) => ({
          method: req.method,
          url: req.url,
          data: req.data,
          headers: req.headers,
          time: new Date().toLocaleString(),
        })),
        __BUILD_DATE__,
        __GIT_HASH__,
      }
      // 过滤掉一些不需要上报的错误
      if (event.exception) {
        const error = hint.originalException
        if (error instanceof Error) {
          // 过滤网络错误和已知错误
          if (
            Object.values(CommonErrorMsg).some((msg) => msg === error.message)
          ) {
            return null
          }
        }
        if (error instanceof String) {
          if (error === CommonErrorMsg.CANCEL_SELECT_ROOM) {
            return null
          }
        }
        clearStacks()
        return event
      }
      clearStacks()
      return event
    }
  }
})()
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: prodEnvSentryDns,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    // 添加额外的错误信息
    beforeSend,
    integrations: (defaultIntegrations) => [
      ...defaultIntegrations,
      Sentry.tanstackRouterBrowserTracingIntegration(router),
    ],
  })
}
document.body.addEventListener(
  'touchmove',
  /**
   * 禁止浏览器下拉回弹
   */
  function stopDrop() {
    let lastY: number | undefined //最后一次y坐标点
    $(document.body).on('touchstart', function (event) {
      lastY = event?.originalEvent?.changedTouches?.[0]?.clientY //点击屏幕时记录最后一次Y度坐标。
    })
    $(document.body).on('touchmove', function (event) {
      const y = event?.originalEvent?.changedTouches?.[0]?.clientY
      const st = $(this).scrollTop() //滚动条高度
      if (y && lastY && st && y >= lastY && st <= 10) {
        //如果滚动条高度小于0，可以理解为到顶了，且是下拉情况下，阻止touchmove事件。
        lastY = y
        event.preventDefault()
      }
      lastY = y
    })
  },
  { passive: false },
) //passive 参数不能省略，用来兼容ios和android

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <TanStackQueryProvider.Provider>
        {shouldShowWatermark() && (
          <Watermark content={`${__BUILD_DATE__}预览版，仅供演示`} />
        )}
        {/* 条件渲染水印组件 */}
        <RouterProvider router={router} />
      </TanStackQueryProvider.Provider>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
